import {
    Box,
    Button,
    Grid,
    Skeleton,
    Theme,
    Typography,
    Hidden
} from "@mui/material";
import {
    CrossIcon,
    SearchIcon,
    ToggleBox,
} from "../../../assets/SvgIcons";
import CustomFilter from "../common/CustomFilter";
import {
    CategoryFilterData,
    navigationData,
    Order,
    PaginationObj,
    SortingObj,
    TableBodyData,
    TableHeaderData
} from "../types";
import MutualFundTableBody, { SchemeBoxResponsiveBody } from "../common/customTable/MutualFundTableBody";
import {
    useDispatch,
    useSelector
} from "react-redux";
import {
    getAmcFilters,
    getCategoryFilters,
    getRiskFilters,
    getSchemesTableData,
    setAppliedFundsFilters,
    setExploreMutualFundsPageVisited,
    setSelectedSchemesObj,
} from "../../../redux-store/actions/investorProfile";
import {
    useEffect,
    useState,
    useCallback
} from "react";
import { useHistory } from "react-router";
import SearchSchemesDialog from "./SearchSchemesDialog";
import CustomTable from "../common/customTable";
import {
    AmcFilter,
    AppliedFundsFilters,
    CategoryFilter,
    GetSchemesTableDataResponseBody,
    RatingFilter,
    RiskFilter,
} from "../../../redux-store/types/investorProfile";
import SelectedSchemesDialog from "./SelectedSchemesDialog";
import FiveSchemesDialog from "./FiveSchemesDialog";
import { RootStateType } from "../../../redux-store/reducers";
import BreadCrum from "../../../lib/BreadCrum";
import { debounce } from 'lodash';
import { showError } from "../../../redux-store/actions/auth";
import { HELDAWAY_PORTFOLIO_STATUS, MAX_SCHEMES_SELECTED } from "../../../utils/constant";
import CustomPopoverFilter from "./filters/CustomPopoverFilterr";
import TablePaginationComponent from "../common/customTable/TablePaginationComponent";
import { isRMLikeRole } from "../../../utils/utilityFunctions";

const DEBOUNCE_DELAY = 1000

const categories = [
    "Equity",
    "Debt",
    "Hybrid",
    "Commodities"
]

//hard coded to be removed later
const ratingFilter = [
    { rating_id: "5", rating_type: "5 Stars" },
    { rating_id: "4", rating_type: "4 Stars" },
    { rating_id: "3", rating_type: "3 Stars" },
    { rating_id: "2", rating_type: "2 Stars" },
    { rating_id: "1", rating_type: "1 Star" },
    { rating_id: "-1", rating_type: "Not Rated" }
];



export default function SelectedCategorySchemes(): JSX.Element {
    const history = useHistory()
    const { pathname: pathName, state } = history.location

    const dispatch = useDispatch()


    const { clientCode = '', selectedCategory = "" } = state as navigationData


    const headersForNfo = [
        { id: "issueOpen", label: "NFO Start Date", align: "center", isSortable: false },
        { id: "issueStatedClose", label: "NFO End Date", align: "center", isSortable: false },
    ] satisfies readonly TableHeaderData[];

    const headersForNormal = [
        { id: "intFundRating", label: "Ratings", align: "center", isSortable: true },
        { id: "year1Return", label: "1Y Returns", align: "center", isSortable: true },
        { id: "year3Return", label: "3Y Returns", align: "center", isSortable: true },
        { id: "year5Return", label: "5Y Returns", align: "center", isSortable: true },
    ] satisfies readonly TableHeaderData[];

    const tableHeaderData: TableHeaderData[] = [
        { id: "schemeName", label: "SCHEME", align: "left", isSortable: true },
        ...(selectedCategory === "NFO" ? headersForNfo : headersForNormal),
    ];


    const {
        role = '',
    } = useSelector((store: RootStateType) => store.auth);
    const {
        selectedPortfolio,
        isPortfolioHeldaway
    } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const { familyMemberId = "" } = useSelector((store: RootStateType) => store.id);

    const {
        appliedFundsFilters = [],
        selectedSchemesObj = {},
        isExploreMutualFundsPageVisited = true,
    } = useSelector((store: RootStateType) => store.investorProfile);

    const [currentCategory, setCurrentCategory] = useState<string>("Equity");

    const [isSelectedSchemesDialogVisible, setSelectedSchemesDialogVisible] = useState<boolean>(false)
    const [isSearchSchemesDialogVisible, setSearchSchemesDialogVisible] = useState<boolean>(false)
    const [isFiveSchemesDialogVisible, setFiveSchemesDialogVisible] = useState<boolean>(false)

    const [isFiltersLoading, setFiltersLoading] = useState<boolean>(false)
    const [amcFilterData, setAmcFilterData] = useState<AmcFilter[]>([])
    const [categoryFilterData, setCategoryFilterData] = useState<CategoryFilterData>({
        Equity: [],
        Hybrid: [],
        Debt: [],
        Commodities: [],
    })
    const [riskFilterData, setRiskFilterData] = useState<RiskFilter[]>([])
    const [ratingFilterData, setRatingFilterData] = useState<RatingFilter[]>([])
    const [isSubCategoryShortcutClicked, setIsSubCategoryShortcutClicked] = useState(false)

    const [isSchemesTableDataLoading, setSchemesTableDataLoading] = useState<boolean>(false)
    const [schemesTableData, setSchemesTableData] = useState<TableBodyData[]>([])
    const [schemesTableSortingObj, setSchemesTableSortingObj] = useState<SortingObj>({
        order: "desc",
        orderBy: "year3Return"
    })

    const [schemesTablePaginationObj, setSchemesTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 10,
        totalRows: 0
    })

    const [selectedSchemesTableSortingObj, setSelectedSchemesTableSortingObj] = useState<SortingObj>({
        order: "desc",
        orderBy: "year3Return"
    })

    const getSchemesTableDataPayload = (data: AppliedFundsFilters[]) => {
        const companyFilter = data
            .filter(obj => obj.filterType.toLowerCase() === "amc")
            .map(obj => `${obj.companyId}`)

        const categoryFilter = data
            .filter(obj => obj.filterType.toLowerCase() === "category" && obj?.subCategoryId?.toLowerCase()?.includes("_all"))
            .map(obj => `${obj.categoryId}`)

        const subCategoryFilter = data
            .filter(obj => obj.filterType.toLowerCase() === "category" && !obj?.subCategoryId?.toLowerCase()?.includes("_all"))
            .map(obj => `${obj.subCategoryId}`)

        const riskFilter = data
            .filter(obj => obj.filterType.toLowerCase() === "risk")
            .map(obj => `${obj.label}`)

        const ratingFilter = data
            .filter(obj => obj.filterType.toLowerCase() === "rating")
            .map(obj => Number(obj?.value))
        if (ratingFilter.includes(-1)) {
            ratingFilter.push(0);
        }
        const categoryFilterWithoutNFO = categoryFilter.filter(item => item !== "-2")
        return {
            ...(companyFilter.length ? { company: companyFilter } : {}),
            ...(categoryFilter.length ? { category: selectedCategory === "NFO" ? ["-2", ...categoryFilterWithoutNFO] : categoryFilter } : {}),
            ...(subCategoryFilter.length ? { subCategory: subCategoryFilter } : {}),
            ...(riskFilter.length ? { riskType: riskFilter } : {}),
            ...(ratingFilter.length ? { rating: ratingFilter } : {}),
            ...(selectedCategory === "NFO" && categoryFilter.length === 0 ? { category: ["-2"] } : {})
        }
    }

    const fetchSchemesTableData = async ({
        sort,
        order,
        currentPage,
        fundsPerPage
    }: {
        sort: string;
        order: Order;
        currentPage: number;
        fundsPerPage: number;
    }) => {
        const payload = {
            sort,
            order,
            currentPage,
            fundsPerPage,
            ...getSchemesTableDataPayload(appliedFundsFilters)
        }

        const { totalLength = 0, currentPageFunds = [] } = await (dispatch(getSchemesTableData(payload)) as unknown as Promise<GetSchemesTableDataResponseBody>)

        const modifiedSchemesTableData = currentPageFunds
            .map((obj) => (obj.scheme in selectedSchemesObj) ?
                {
                    ...obj,
                    scheme: `${obj.scheme}`,
                    checked: true
                } : {
                    ...obj,
                    scheme: `${obj.scheme}`,
                    checked: false
                })

        setSchemesTableData(modifiedSchemesTableData)
        setSchemesTableSortingObj({
            order,
            orderBy: sort
        })
        setSchemesTablePaginationObj({
            currentPage: currentPage - 1,
            rowsPerPage: fundsPerPage,
            totalRows: totalLength
        })
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                setFiltersLoading(true)
                // if (isExploreMutualFundsPageVisited) {
                //     setSchemesTableDataLoading(true)
                // }

                const appliedAmcFilters = appliedFundsFilters
                    .filter((obj: AppliedFundsFilters) => obj.filterType.toLowerCase() === "amc")
                    .map((obj: AppliedFundsFilters) => `${obj.companyId}`)

                const appliedCategoryAllFilters = appliedFundsFilters
                    .filter((obj: AppliedFundsFilters) =>
                        obj.filterType.toLowerCase() === "category" &&
                        obj.subCategoryId?.toLowerCase().includes("_all"))
                    .map((obj: AppliedFundsFilters) => `${obj.subCategoryId}`)

                const appliedSubCategoryFilters = appliedFundsFilters
                    .filter((obj: AppliedFundsFilters) =>
                        obj.filterType.toLowerCase() === "category" &&
                        !obj.subCategoryId?.toLowerCase().includes("_all"))
                    .map((obj: AppliedFundsFilters) => `${obj.subCategoryId}`)

                const appliedRiskFilters = appliedFundsFilters
                    .filter((obj: AppliedFundsFilters) => obj.filterType.toLowerCase() === "risk")
                    .map((obj: AppliedFundsFilters) => `${obj.riskId}`)

                const appliedRatingFilters = appliedFundsFilters
                    .filter((obj: AppliedFundsFilters) => obj.filterType.toLowerCase() === "rating")
                    .map((obj: AppliedFundsFilters) => `${obj.ratingId}`)

                const amcFilterData = await (dispatch(getAmcFilters()) as unknown as Promise<AmcFilter[]>)
                const modifiedAmcFilterData = amcFilterData?.sort((a, b) => a?.companyName?.localeCompare(b?.companyName))?.map((obj: AmcFilter) => ({
                    ...obj,
                    companyId: `${obj.companyId}`,
                    checked: appliedAmcFilters.includes(`${obj.companyId}`)
                }))

                setAmcFilterData(modifiedAmcFilterData)

                const categoryFilterData = await
                    (dispatch(getCategoryFilters()) as unknown as Promise<CategoryFilter[]>)

                const equityCategoryFilterData = [
                    {
                        categoryId: '1',
                        categoryName: 'Equity',
                        subCategoryId: "equity_all",
                        subCategoryName: 'Equity',
                        checked: appliedCategoryAllFilters.includes("equity_all")
                    },
                    ...categoryFilterData
                        .filter((obj: CategoryFilter) => `${obj.categoryId}` === '1')
                        .map((obj: CategoryFilter) => ({
                            ...obj,
                            categoryId: `${obj.categoryId}`,
                            subCategoryId: `${obj.subCategoryId}`,
                            checked: appliedCategoryAllFilters.includes("equity_all") || appliedSubCategoryFilters.includes(`${obj.subCategoryId}`)
                        }))
                ]

                const debtCategoryFilterData = [
                    {
                        categoryId: '2',
                        categoryName: 'Debt',
                        subCategoryId: "debt_all",
                        subCategoryName: 'Debt',
                        checked: appliedCategoryAllFilters.includes("debt_all")
                    },
                    ...categoryFilterData
                        .filter((obj: CategoryFilter) => `${obj.categoryId}` === '2')
                        .map((obj: CategoryFilter) => ({
                            ...obj,
                            categoryId: `${obj.categoryId}`,
                            subCategoryId: `${obj.subCategoryId}`,
                            checked: appliedCategoryAllFilters.includes("debt_all") || appliedSubCategoryFilters.includes(`${obj.subCategoryId}`)
                        }))
                ]
                const hybridCategoryFilterData = [
                    {
                        categoryId: '3',
                        categoryName: 'Hybrid',
                        subCategoryId: "hybrid_all",
                        subCategoryName: 'Hybrid',
                        checked: appliedCategoryAllFilters.includes("hybrid_all")
                    },
                    ...categoryFilterData
                        .filter((obj: CategoryFilter) => `${obj.categoryId}` === '3')
                        .map((obj: CategoryFilter) => ({
                            ...obj,
                            categoryId: `${obj.categoryId}`,
                            subCategoryId: `${obj.subCategoryId}`,
                            checked: appliedCategoryAllFilters.includes("hybrid_all") || appliedSubCategoryFilters.includes(`${obj.subCategoryId}`)
                        }))
                ]

                const commoditiesCategoryFilterData = [
                    {
                        categoryId: '4',
                        categoryName: 'Commodities',
                        subCategoryId: "commodities_all",
                        subCategoryName: 'Commodities',
                        checked: appliedCategoryAllFilters.includes("commodities_all")
                    },
                    ...categoryFilterData
                        .filter((obj: CategoryFilter) => `${obj.categoryId}` === '4')
                        .map((obj: CategoryFilter) => ({
                            ...obj,
                            categoryId: `${obj.categoryId}`,
                            subCategoryId: `${obj.subCategoryId}`,
                            checked: appliedCategoryAllFilters.includes("commodities_all") || appliedSubCategoryFilters.includes(`${obj.subCategoryId}`)
                        }))
                ]


                setCategoryFilterData({
                    Equity: equityCategoryFilterData,
                    Hybrid: hybridCategoryFilterData,
                    Debt: debtCategoryFilterData,
                    Commodities: commoditiesCategoryFilterData,
                })

                const riskFilterData = await (dispatch(getRiskFilters()) as unknown as Promise<RiskFilter[]>)
                const modifiedRiskFilterData = riskFilterData.map((obj: RiskFilter) => ({
                    ...obj,
                    riskId: `${obj.risk_id}`,
                    checked: appliedRiskFilters.includes(`${obj.risk_id}`)
                }))

                setRiskFilterData(modifiedRiskFilterData)

                // const ratingsFilterData = await (dispatch(getRatingsFilters()) as unknown as Promise<RatingFilter[]>)
                const ratingFilterData = await ratingFilter;
                const modifiedRatingFilterData = ratingFilterData.map((obj: RatingFilter) => ({
                    ...obj,
                    ratingId: `${obj.rating_id}`,
                    checked: appliedRatingFilters.includes(`${obj.rating_id}`)
                }))

                setRatingFilterData(modifiedRatingFilterData);

            } catch (e) {
                console.log("Error: ", e)
            } finally {
                setFiltersLoading(false);
            }
        }

        fetchData()
    }, [])

    useEffect(() => {
        if (isSubCategoryShortcutClicked) {

            const modifiedCategoryFilter = Object.keys(categoryFilterData)
                .flatMap(category => {
                    const subCategoryAllValue = `${category.toLowerCase()}_all`
                    const categoryArray = categoryFilterData[category];

                    const allCheckedObject = categoryArray.find(obj =>
                        obj.subCategoryId === subCategoryAllValue
                        && obj.checked
                    );

                    if (allCheckedObject) {
                        return [{
                            label: allCheckedObject.categoryName,
                            value: allCheckedObject.subCategoryId,
                            filterType: 'category',
                            filterTypeValue: `category-${allCheckedObject.subCategoryId}`,
                            categoryId: allCheckedObject.categoryId,
                            categoryName: allCheckedObject.categoryName,
                            subCategoryId: allCheckedObject.subCategoryId,
                            subCategoryName: allCheckedObject.subCategoryName
                        }];
                    }
                    else {
                        return categoryArray
                            .filter(obj => obj.checked)
                            .map(obj => ({
                                label: obj.subCategoryName,
                                value: obj.subCategoryId,
                                filterType: 'category',
                                filterTypeValue: `category-${obj.subCategoryId}`,
                                categoryId: obj.categoryId,
                                categoryName: obj.categoryName,
                                subCategoryId: obj.subCategoryId,
                                subCategoryName: obj.subCategoryName
                            }));
                    }
                })

            dispatch(setAppliedFundsFilters(modifiedCategoryFilter));

            setIsSubCategoryShortcutClicked(false)
        }
    }, [isSubCategoryShortcutClicked, categoryFilterData])

    useEffect(() => {
        const fetchData = async () => {
            try {
                setSchemesTableDataLoading(true)
                await fetchSchemesTableData({
                    sort: schemesTableSortingObj.orderBy,
                    order: schemesTableSortingObj.order,
                    currentPage: 1,
                    fundsPerPage: 10
                })

            } catch (e) {
                console.log("Error: ", e)
            } finally {
                setSchemesTableDataLoading(false)
            }
        }

        fetchData()
    }, [appliedFundsFilters])

    const handleApplyFilters = (
        currentAmcFilterData: AmcFilter[],
        currentCategoryFilterData: CategoryFilterData,
        currentRiskFilterData: RiskFilter[],
        currentRatingFilterData: RatingFilter[]
    ) => {
        const appliedAmcFilters = currentAmcFilterData
            .filter(obj => obj.checked)
            .map(obj => ({
                label: obj.companyName,
                value: obj.companyId,
                filterType: "amc",
                filterTypeValue: `amc-${obj.companyId}`,
                companyId: obj.companyId,
                companyName: obj.companyName
            }))

        const appliedCategoryFilters = Object.keys(currentCategoryFilterData)
            .flatMap(category => {
                const subCategoryAllValue = `${category.toLowerCase()}_all`
                const categoryArray = currentCategoryFilterData[category];
                const allCheckedObject = categoryArray.find(obj => obj.subCategoryId === subCategoryAllValue && obj.checked);

                if (allCheckedObject) {
                    return [{
                        label: allCheckedObject.categoryName,
                        value: allCheckedObject.subCategoryId,
                        filterType: "category",
                        filterTypeValue: `category-${allCheckedObject.subCategoryId}`,
                        categoryId: allCheckedObject.categoryId,
                        categoryName: allCheckedObject.categoryName,
                        subCategoryId: allCheckedObject.subCategoryId,
                        subCategoryName: allCheckedObject.subCategoryName
                    }];
                }
                else {
                    return categoryArray
                        .filter(obj => obj.checked)
                        .map(obj => ({
                            label: obj.subCategoryName,
                            value: obj.subCategoryId,
                            filterType: "category",
                            filterTypeValue: `category-${obj.subCategoryId}`,
                            categoryId: obj.categoryId,
                            categoryName: obj.categoryName,
                            subCategoryId: obj.subCategoryId,
                            subCategoryName: obj.subCategoryName
                        }));
                }
            });

        const appliedRiskFilters = currentRiskFilterData
            .filter(obj => obj.checked)
            .map(obj => ({
                label: obj.risk_type,
                value: obj.risk_id,
                filterType: "risk",
                filterTypeValue: `risk-${obj.risk_id}`,
                riskId: obj.risk_id,
                riskName: obj.risk_type
            }))

        const appliedRatingFilters = currentRatingFilterData
            .filter(obj => obj.checked)
            .map(obj => ({
                label: obj.rating_type,
                value: obj.rating_id,
                filterType: "rating",
                filterTypeValue: `rating-${obj.rating_id}`,
                ratingId: obj.rating_id,
                ratingName: obj.rating_type
            }))

        dispatch(setAppliedFundsFilters([...appliedAmcFilters, ...appliedCategoryFilters, ...appliedRiskFilters, ...appliedRatingFilters]));

        // window.scrollTo(0, 0);
    }

    const debouncedHandleApplyFilters = useCallback(
        debounce(handleApplyFilters, DEBOUNCE_DELAY),
        []
    );

    const handleAmcFilterChange = (value: string, checked: boolean) => {
        const updatedAmcFilterData = amcFilterData.map(item => item.companyId === value ? { ...item, checked } : item);

        setAmcFilterData(updatedAmcFilterData)

        dispatch(setExploreMutualFundsPageVisited(true))

        setSchemesTableDataLoading(true)

        debouncedHandleApplyFilters(updatedAmcFilterData, categoryFilterData, riskFilterData, ratingFilterData)
    }

    const handleCategoryFilterChange = (subCategory: string, currentValue: string, checked: boolean) => {

        const currentSubCategory = categoryFilterData[subCategory]
        const subCategoryAllValue = `${subCategory.toLowerCase()}_all`

        let updatedSubCategory = currentSubCategory.map(item => {
            if (currentValue === subCategoryAllValue) {
                return { ...item, checked };
            }
            if (item.subCategoryId === currentValue) {
                return { ...item, checked };
            }
            return item;
        });

        const isAllChecked = !!updatedSubCategory.find(obj => obj.subCategoryId === subCategoryAllValue)?.checked;

        const areAllSubFiltersChecked = updatedSubCategory
            .filter(obj => obj.subCategoryId !== subCategoryAllValue)
            .every(item => item.checked);

        const isAnySubFilterUnchecked = updatedSubCategory
            .some(item => !item.checked && item.subCategoryId !== subCategoryAllValue);

        if (areAllSubFiltersChecked && checked) {
            updatedSubCategory = updatedSubCategory.map(item => {
                if (item.subCategoryId === subCategoryAllValue) {
                    return { ...item, checked };
                }
                return item;
            });
        }

        if (isAllChecked && isAnySubFilterUnchecked && !checked) {
            updatedSubCategory = updatedSubCategory.map(item => {
                if (item.subCategoryId === subCategoryAllValue) {
                    return { ...item, checked: false };
                }
                return item;
            });
        }

        const updatedCategoryFilterData = {
            ...categoryFilterData,
            [subCategory]: updatedSubCategory
        };

        setCategoryFilterData(updatedCategoryFilterData)

        dispatch(setExploreMutualFundsPageVisited(true))

        setSchemesTableDataLoading(true)

        debouncedHandleApplyFilters(amcFilterData, updatedCategoryFilterData, riskFilterData, ratingFilterData)
    }

    const handleRiskFilterChange = (value: string, checked: boolean) => {
        const updatedRiskFilterData = riskFilterData.map(item => item.risk_id === value ? { ...item, checked } : item);

        setRiskFilterData(updatedRiskFilterData)

        dispatch(setExploreMutualFundsPageVisited(true))

        setSchemesTableDataLoading(true)

        debouncedHandleApplyFilters(amcFilterData, categoryFilterData, updatedRiskFilterData, ratingFilterData)
    }

    const handleRatingFilterChange = (value: string, checked: boolean) => {
        const updatedRatingFilterData = ratingFilterData.map(item => item.rating_id === value ? { ...item, checked } : item);

        setRatingFilterData(updatedRatingFilterData)

        dispatch(setExploreMutualFundsPageVisited(true))

        setSchemesTableDataLoading(true)

        debouncedHandleApplyFilters(amcFilterData, categoryFilterData, riskFilterData, updatedRatingFilterData)
    }

    const handleFilterLeavesClick = (
        currentFilterType: string,
        currentFilterTypeValue: string,
        currentValue: string
    ) => {
        const modifiedAppliedFundsFilters = appliedFundsFilters.filter(obj => obj.filterTypeValue !== currentFilterTypeValue)

        dispatch(setAppliedFundsFilters(modifiedAppliedFundsFilters));

        setAmcFilterData(prevState => prevState
            .map(obj => currentFilterType === "amc" && obj.companyId === currentValue ? { ...obj, checked: false } : obj)
        )

        setCategoryFilterData(prevState => {
            const newState: CategoryFilterData = {};

            Object.keys(prevState)
                .forEach(category => {
                    newState[category] = prevState[category].map(item => ({
                        ...item,
                        checked: currentValue === `${category.toLowerCase()}_all` ? false
                            : currentFilterType === "category" && item.subCategoryId === currentValue ? false
                                : item.checked
                    }));
                });

            return newState;
        })

        setRiskFilterData(prevState => prevState
            .map(obj => currentFilterType === "risk" && obj.risk_id === currentValue ? { ...obj, checked: false } : obj)
        )

        setRatingFilterData(prevState => prevState
            .map(obj => currentFilterType === "rating" && obj.rating_id === currentValue ? { ...obj, checked: false } : obj)
        )
    }

    const handleSubCategoriesShortcutClick = (currentSubCategory: string, currentValue: string) => {
        setSchemesTableDataLoading(true)

        const subCategoryAllValue = `${currentSubCategory.toLowerCase()}_all`

        setIsSubCategoryShortcutClicked(true)

        setCategoryFilterData(prevState => {
            const newState = { ...prevState };

            Object.keys(newState).forEach(subCategory => {
                newState[subCategory] = newState[subCategory].map(item => {
                    if (subCategory === currentSubCategory && currentValue === subCategoryAllValue) {
                        return { ...item, checked: true };
                    }
                    else if (subCategory === currentSubCategory && item.subCategoryId === currentValue) {
                        return { ...item, checked: true };
                    }
                    else {
                        return { ...item, checked: false };
                    }
                });
            });

            return newState;
        });

        dispatch(setExploreMutualFundsPageVisited(true))
    }

    const handleClearAll = () => {
        setAmcFilterData(prevState => prevState.map(item => ({ ...item, checked: false })))

        setCategoryFilterData(prevState => {
            const newState: CategoryFilterData = {};

            Object.keys(prevState).forEach(category => {
                newState[category] = prevState[category].map(item => {
                    return { ...item, checked: false };
                });
            });

            return newState;
        })

        setRiskFilterData(prevState => prevState.map(item => ({ ...item, checked: false })))
        setRatingFilterData(prevState => prevState.map(item => ({ ...item, checked: false })))

        dispatch(setAppliedFundsFilters([]))
    }

    const handleSchemesTableSort = async (orderBy: string, order: Order) => {
        try {
            setSchemesTableDataLoading(true)


            await fetchSchemesTableData({
                sort: orderBy,
                order,
                currentPage: 1,
                fundsPerPage: schemesTablePaginationObj.rowsPerPage
            })
        } catch (e) {
            console.log("Error: ", e)
        } finally {
            setSchemesTableDataLoading(false)
        }
    }

    const handleSchemesTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            setSchemesTableDataLoading(true)


            await fetchSchemesTableData({
                sort: schemesTableSortingObj.orderBy,
                order: schemesTableSortingObj.order,
                currentPage: 1,
                fundsPerPage: Number(event.target.value),
            })
        } catch (e) {
            console.log("Error: ", e)
        } finally {
            setSchemesTableDataLoading(false)
        }
    }

    const handleSchemesTablePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        try {
            setSchemesTableDataLoading(true)


            await fetchSchemesTableData({
                sort: schemesTableSortingObj.orderBy,
                order: schemesTableSortingObj.order,
                currentPage: page + 1,
                fundsPerPage: schemesTablePaginationObj.rowsPerPage,
            })
        } catch (e) {
            console.log("Error: ", e)
        } finally {
            setSchemesTableDataLoading(false)
        }
    }

    const handleSearchMutualFundsClick = () => {
        if (!isFiltersLoading && !isSchemesTableDataLoading) {
            setSearchSchemesDialogVisible(true)
        }
    }

    const handleSearchSchemesDialogClose = () => {
        setSearchSchemesDialogVisible(false)
    }

    const handleSearchSchemesDialogProceedClick = async () => {
        try {
            setSearchSchemesDialogVisible(false)
            setSchemesTableDataLoading(true)
            handleRecommendClick()
            // dispatch(setExploreMutualFundsPageVisited(true))

            // await fetchSchemesTableData({
            //     sort: schemesTableSortingObj.orderBy,
            //     order: schemesTableSortingObj.order,
            //     currentPage: schemesTablePaginationObj.currentPage + 1,
            //     fundsPerPage: schemesTablePaginationObj.rowsPerPage
            // })
        } catch (e) {
            console.log("Error: ", e)
        } finally {
            setSchemesTableDataLoading(false)
        }
    }

    const handleSelectedSchemesDialogClose = () => {
        setSelectedSchemesDialogVisible(false)
        setSelectedSchemesTableSortingObj({
            order: "desc",
            orderBy: "year3Return"
        })
        const modifiedSelectedSchemesObj = Object.fromEntries(
            Object.entries(selectedSchemesObj).filter(([key, value]) => value.checked)
        )

        dispatch(setSelectedSchemesObj(modifiedSelectedSchemesObj))
    }

    const handleFiveSchemesDialogClose = () => {
        setFiveSchemesDialogVisible(false)
    }

    const handleSchemeTableSchemeClick = (schemeObj: TableBodyData) => {
        dispatch(setSelectedSchemesObj({
            [schemeObj.scheme]: { ...schemeObj, checked: true }
        }))
        history.push(`${pathName}/${schemeObj.scheme}`, { selectedSchemes: [schemeObj], clientCode: clientCode, selectedCategory })
    }

    const handleSchemeTableCheckboxClick = (schemeId: string, checked: boolean) => {
        const isSelected = schemeId in selectedSchemesObj;

        if (Object.keys(selectedSchemesObj).length === MAX_SCHEMES_SELECTED && checked) {
            setFiveSchemesDialogVisible(true)
            return;
        } else {
            if (isSelected && !checked) {
                const { [schemeId]: _, ...modifiedSelectedSchemesObj } = selectedSchemesObj;
                dispatch(setSelectedSchemesObj(modifiedSelectedSchemesObj))
            }

            if (!isSelected && checked) {
                const modifiedSelectedSchemesObj = {
                    ...selectedSchemesObj,
                    [schemeId]: {
                        ...schemesTableData
                            .filter((obj: TableBodyData) => obj.scheme === schemeId)
                            .map((obj: TableBodyData) => ({ ...obj, checked: true }))[0]
                    }
                }
                dispatch(setSelectedSchemesObj(modifiedSelectedSchemesObj))
            }

            setSchemesTableData((prevState: TableBodyData[]) => prevState
                .map((obj: TableBodyData) => {
                    return obj.scheme === schemeId ? { ...obj, checked } : obj
                }))
        }
    }

    const handleClearSelectedSchemes = () => {
        setSchemesTableData(prevState => prevState.map(obj => ({ ...obj, checked: false })))

        dispatch(setSelectedSchemesObj({}))
    }

    const handleSelectedSchemesTableCheckboxClick = (schemeId: string, checked: boolean) => {
        const modifiedSelectedSchemesObj = {
            ...selectedSchemesObj,
            [schemeId]: {
                ...selectedSchemesObj[schemeId],
                checked
            }
        }

        dispatch(setSelectedSchemesObj(modifiedSelectedSchemesObj))

        setSchemesTableData(prevState => prevState.map((obj: TableBodyData) => ({
            ...obj,
            checked: obj.scheme === schemeId ? checked : obj.checked
        })))

    }

    const handleSelectedSchemesTableSchemeClick = (schemeObj: TableBodyData) => {
        handleSelectedSchemesDialogClose()

        dispatch(setSelectedSchemesObj({
            [schemeObj.scheme]: { ...schemeObj, checked: true }
        }))

        history.push(`${pathName}/${schemeObj.scheme}`, { selectedSchemes: [schemeObj], clientCode: clientCode })
    }

    const handleRecommendClick = () => {
        if (isPortfolioHeldaway) {
            dispatch(showError(HELDAWAY_PORTFOLIO_STATUS))
            return
        }

        const selectedSchemes = Object.values(selectedSchemesObj)

        const schemeDetails = selectedSchemes?.map((obj: TableBodyData) => {
            return {
                scheme_id: obj?.scheme ?? "-",
                isReinvestment: obj?.divReinvestAllowed ?? "-"
            }
        })

        history.push(`${pathName}/investment-details`, { selectedSchemes, clientCode, schemeDetails, selectedCategory })
    }

    const selectedSchemesTableData = Object.values(selectedSchemesObj).sort((
        objOne,
        objTwo
    ) => {
        const orderByObjOne = objOne[selectedSchemesTableSortingObj.orderBy];
        const orderByObjTwo = objTwo[selectedSchemesTableSortingObj.orderBy];

        if (orderByObjOne < orderByObjTwo) {
            return selectedSchemesTableSortingObj.order === 'asc' ? -1 : 1;
        }
        if (orderByObjOne > orderByObjTwo) {
            return selectedSchemesTableSortingObj.order === 'asc' ? 1 : -1;
        }
        return 0;
    })

    const initialRouter = pathName.split('/').filter((item) => item !== '')

    const rmRouterPath = [
        {
            name: 'Dashboard',
            path: '/dashboard',
        },
        ...(!initialRouter.includes('product') ? [{
            name: initialRouter[1] + `${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode
            }
        }] : []),
        {
            name: initialRouter[0] === 'product' ? "Mutual Funds" : 'Detailed Portfolio Holdings',
            path: initialRouter[0] === 'product'
                ? `/${initialRouter[0]}/${initialRouter[1]}`
                : `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: {
                clientCode
            },
            ...(initialRouter[0] !== 'product' ? {
                search: `tab=Overview&portfolio=${selectedPortfolio?.value ?? ""}`
            } : {})
        },
        {
            name: 'Explore Mutual Funds',
            path: initialRouter[0] === 'product'
                ? `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`
                : `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}`,
            state: {
                clientCode
            }
        },
        {
            name: 'Selected Category Funds',
            path: initialRouter[0] === 'product'
                ? `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`
                : `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}/${initialRouter[4]}`,
            state: {
                clientCode,
            }
        },
    ]

    const investorRouterPath = [
        ...(familyMemberId
            ?
            [{
                name: 'View Family',
                path: `/investor-family/family-profile/${familyMemberId}`,
            },
            {
                name: `${initialRouter[1]}`,
                path: `/${initialRouter[0]}/${initialRouter[1]}`,
            }]
            :
            [{
                name: 'Dashboard' + `${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
                path: '/investor-dashboard',
            }]
        ),
        {
            name: 'Detailed Portfolio Holdings',
            path: (familyMemberId ? `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}` : `/${initialRouter[0]}/${initialRouter[1]}`),
            state: {
                clientCode
            },
            search: `tab=Overview&portfolio=${selectedPortfolio?.value ?? ""}`
        },
        {
            name: 'Explore Mutual Funds',
            path: (familyMemberId ? `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}` : `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`),
            state: {
                clientCode
            }
        },
        {
            name: 'Selected Category Funds',
            path: (familyMemberId ? `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}/${initialRouter[4]}` : `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}`),
            state: {
                clientCode
            }
        },
    ]

    const isFilterApplied = amcFilterData.some(obj => obj.checked)
        || Object.values(categoryFilterData)
            .reduce((acc, curr) => ([...acc, ...curr]), [])
            .some(obj => obj.checked)
        || riskFilterData.some(obj => obj.checked) || ratingFilterData.some(obj => obj.checked)

    return (
        <>
            <Box>
                <BreadCrum array={isRMLikeRole(role) ? rmRouterPath : investorRouterPath} />
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        marginTop: '4px'
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={(theme: Theme) => ({ color: theme.palette.text.primary, mb: { xs: "10px", sm: "0px" } })}
                    >
                        Explore Mutual Funds
                    </Typography>
                    <Box
                        role="button"
                        tabIndex={0}
                        aria-label="Search for Mutual Funds"
                        aria-disabled={isFiltersLoading || isSchemesTableDataLoading}
                        sx={(theme: Theme) => ({
                            minWidth: { xs: '100%', sm: '300px' },
                            borderRadius: '57px',
                            height: '47.17px',
                            border: `1px solid ${theme.palette.text.secondary}B2`,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px',
                            cursor: ((isFiltersLoading || isSchemesTableDataLoading) ? "not-allowed" : "pointer"),
                           opacity: ((isFiltersLoading || isSchemesTableDataLoading) ? 0.5 : 1),
                            '&:focus-visible': {
                                outline: `2px solid ${theme.palette.primary[500]}`,
                                outlineOffset: '2px',
                            }
                        })}
                        onClick={handleSearchMutualFundsClick}
                         onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleSearchMutualFundsClick();
                            }
                        }}
                    >
                        <SearchIcon />
                        <Typography
                            sx={{
                                color: 'text.secondary',
                                paddingLeft: '10px',
                                cursor: ((isFiltersLoading || isSchemesTableDataLoading) ? "not-allowed" : "pointer")
                            }}
                            variant="subtitle_400">
                            Search for Mutual Funds...
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0.5,
                    mt: 0.5
                }}>
                    <Box
                        sx={{
                            position: 'relative',
                            top: 0,
                            alignSelf: 'flex-start',
                            flexWrap: 'wrap',
                            width: '80%',
                            zIndex: 'auto',
                            display: 'flex',
                            gap: 2,
                        }}
                    >
                        <CustomPopoverFilter
                            isLoading={false}
                            filterData={categoryFilterData}
                            handleFilterChange={handleCategoryFilterChange}
                            title="Category"
                            hasSubFilters
                            getLabel={(item) => item?.subCategoryName}
                            getValue={(item) => item?.subCategoryId}
                            selectedFilters={Object.values(categoryFilterData)
                                .reduce((acc, curr) => ([...acc, ...curr]), [])
                                .filter(obj => obj.checked)}
                        />
                        <CustomPopoverFilter
                            isLoading={false}
                            filterData={riskFilterData}
                            handleFilterChange={handleRiskFilterChange}
                            title="Risk"
                            hasSubFilters={false}
                            getLabel={(item) => item?.risk_type}
                            getValue={(item) => item?.risk_id}
                            selectedFilters={riskFilterData.filter(obj => obj.checked)}
                        />
                        <CustomPopoverFilter
                            isLoading={false}
                            filterData={ratingFilterData}
                            handleFilterChange={handleRatingFilterChange}
                            title='Rating'
                            getLabel={(item) => item?.rating_type}
                            getValue={(item) => item?.ratingId}
                            selectedFilters={ratingFilterData.filter(obj => obj.checked)}
                        />
                        <CustomPopoverFilter
                            isLoading={false}
                            filterData={amcFilterData}
                            handleFilterChange={handleAmcFilterChange}
                            title="AMC"
                            hasSubFilters={false}
                            searchable
                            getLabel={(item) => item?.companyName}
                            getValue={(item) => item?.companyId}
                            selectedFilters={amcFilterData.filter(obj => obj.checked)}
                        />

                        {/* NFO Label start */}
                        {selectedCategory === "NFO" && (
                            <Box
                                sx={(theme: Theme) => ({
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    border: `1px solid ${theme.palette.primary[500]}`,
                                    borderRadius: '25px',
                                    padding: '5px 20px',
                                    backgroundColor: theme.palette.primary[100],
                                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                                    color: theme.palette.text.primary,
                                    ...theme.typography.body_500,
                                })}
                            >
                                NFO
                            </Box>
                        )}
                        {/* NFO Label end */}
                    </Box>

                   <Typography
                        variant="body_600"
                        role="button"
                        tabIndex={0}
                        aria-label="Clear all filters"
                        aria-disabled={!isFilterApplied}
                        sx={(theme: Theme) => ({
                            color: !isFilterApplied ? theme.palette.text.disabled : theme.palette.primary[500],
                            cursor: !isFilterApplied ? 'not-allowed' : 'pointer',
                            '&:focus-visible': {
                                outline: `2px solid ${theme.palette.primary[500]}`,
                                outlineOffset: '2px',
                                borderRadius: '4px',
                            }
                        })}
                        onClick={handleClearAll}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if ((e.key === 'Enter' || e.key === ' ') && isFilterApplied) {
                                e.preventDefault();
                                handleClearAll();
                            }
                        }}
                    >
                        Clear All
                    </Typography>
                </Box>
                <Grid
                    container
                    sx={(theme: Theme) => ({
                        marginTop: { xs: "20px", sm: "15px" },
                        borderRadius: "20px",
                        backgroundColor: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                        padding: { xs: "15px", sm: "15px" },
                        height: `calc(100vh - 210px)`,
                    })}
                    rowSpacing={5}
                >
                    {/* <Grid
                        item
                        xs={12}
                        lg={3}
                        sx={(theme: Theme) => ({
                            paddingTop: "0px !important",
                            paddingRight: { lg: "25px" },
                            borderRightWidth: {
                                xs: "0px",
                                lg: "0.7px",
                            },
                            borderRightStyle: "solid",
                            borderRightColor: `${theme.palette.text.secondary}B2`
                        })}
                    >
                        <CustomFilter
                            isLoading={isFiltersLoading}
                            handleClearAll={handleClearAll}
                            // handleApplyFilters={handleApplyFilters}
                            amcFilterData={amcFilterData}
                            categoryFilterData={categoryFilterData}
                            riskFilterData={riskFilterData}
                            ratingFilterData={ratingFilterData}
                            handleAmcFilterChange={handleAmcFilterChange}
                            handleCategoryFilterChange={handleCategoryFilterChange}
                            handleRiskFilterChange={handleRiskFilterChange}
                            handleRatingFilterChange={handleRatingFilterChange}
                            isFilterApplied={isFilterApplied}
                            defaultExpandedFilter="amc"
                            showApplyButton={false}
                        />
                    </Grid> */}
                    <Grid
                        item
                        xs={12}
                        // lg={9}
                        sx={{
                            paddingTop: { xs: "10px !important", lg: "0px !important" },
                            height: '100%',
                            position: "relative"

                        }}
                    >
                        {/* {!isExploreMutualFundsPageVisited &&
                            <Box
                                sx={{
                                    paddingLeft: {
                                        lg: "30px"
                                    }
                                }}
                            >
                                <Typography
                                    variant="h2"
                                    sx={
                                        (theme: Theme) => ({
                                            color: theme.palette.common.black
                                        })
                                    }
                                >
                                    Categories
                                </Typography>
                                <Typography
                                    variant="subtitle_400"
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.secondary,
                                        marginTop: "15px"
                                    })}
                                >
                                    Specify a category to select your preferred schemes{isRMLikeRole(role) ? " for initial recommendations." : "."}
                                </Typography>
                                <Box
                                    sx={{
                                        marginTop: "35px",
                                        display: "flex",
                                        gap: "15px",
                                        flexWrap: "wrap"
                                    }}
                                >
                                    {isFiltersLoading && (
                                        [...Array(4)].map((_, index) => (
                                            <Skeleton
                                                key={index}
                                                animation="wave"
                                                variant="rounded"
                                                height={65}
                                                sx={{
                                                    borderRadius: "7px",
                                                    flex: 1,
                                                    minWidth: "50px",
                                                }}
                                            />
                                        ))
                                    )}
                                    {!isFiltersLoading && (
                                        categories.map((category) => (
                                            <Box
                                                key={category}
                                                sx={(theme: Theme) => ({
                                                    padding: "5px 15px",
                                                    borderRadius: "7px",
                                                    backgroundColor: currentCategory === category ? theme.palette.primary[500] : theme.palette.grey[50],
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    cursor: "pointer",
                                                    minWidth: { xs: '100px', sm: '80px', md: "50px" },
                                                    height: "65px",
                                                    flex: 1
                                                })}
                                                onClick={() => setCurrentCategory(category)}
                                            >
                                                <Typography
                                                    variant="body_500"
                                                    sx={(theme: Theme) => ({
                                                        color: currentCategory === category ? theme.palette.textDark.primary : theme.palette.text.secondary
                                                    })}
                                                >
                                                    {category}
                                                </Typography>
                                            </Box>
                                        ))
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        marginTop: "35px",
                                        display: "flex",
                                        columnGap: "25px",
                                        rowGap: "30px",
                                        flexWrap: "wrap",
                                        padding: "0px 35px"
                                    }}
                                >
                                    {isFiltersLoading && (
                                        [...Array(12)].map((_, index) => (
                                            <Skeleton
                                                key={index}
                                                animation="wave"
                                                variant="rounded"
                                                height={35}
                                                width={120}
                                                sx={{
                                                    borderRadius: "15px"
                                                }}
                                            />
                                        ))
                                    )}
                                    {!isFiltersLoading && (
                                        categoryFilterData[currentCategory as keyof typeof categoryFilterData].map(
                                            (obj) => (
                                                <Button
                                                    key={obj.subCategoryId}
                                                    variant="outlined"
                                                    sx={(theme: Theme) => ({
                                                        padding: "2px 15px",
                                                        border: `1px solid ${theme.palette.text.secondary}`,
                                                        borderRadius: "15px",
                                                        ...theme.typography.body_500,
                                                        color: theme.palette.text.secondary,
                                                        fontSize: "18px",
                                                        "&:hover": {
                                                            borderColor: theme.palette.text.primary,
                                                            backgroundColor: theme.palette.grey[100]
                                                        }
                                                    })}
                                                    onClick={() => handleSubCategoriesShortcutClick(currentCategory, obj.subCategoryId)}
                                                >
                                                    {obj.subCategoryId === `${currentCategory.toLowerCase()}_all` ?
                                                        "All" : obj.subCategoryName}
                                                </Button>
                                            ))
                                    )}
                                </Box>
                            </Box>
                        } */}
                        {/* {isExploreMutualFundsPageVisited && */}
                        <Box sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    // marginLeft: { lg: "30px" },
                                    alignItems: "baseline",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap"
                                }}
                            >
                                {isSchemesTableDataLoading && (
                                    <Skeleton
                                        animation="wave"
                                        width={240}
                                    />
                                )}
                                {!isSchemesTableDataLoading && (
                                    <Typography
                                        variant="h2"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.common.black,
                                            alignSelf: 'baseline'
                                        })}
                                    >
                                        Search Results: {schemesTablePaginationObj.totalRows} Funds
                                    </Typography>
                                )}
                                {initialRouter[0] !== 'product' &&
                                    (
                                        <Button
                                            onClick={handleRecommendClick}
                                            variant="contained"
                                            sx={(theme: Theme) => ({
                                                mt: { xs: 3, sm: 0 },
                                                width: { xs: '100%', sm: 'unset' },
                                                borderRadius: { xs: "5px", sm: "10px" },
                                                padding: { xs: "5px 100px", sm: "7px 75px" },
                                                fontSize: { xs: '12px' },
                                                backgroundColor: theme.palette.primary[500],
                                                color: theme.palette.textDark.primary,
                                                "&.Mui-disabled": {
                                                    backgroundColor: `${theme.palette.text.secondary}B2`,
                                                    color: theme.palette.text.secondary,
                                                    cursor: "not-allowed !important",
                                                    pointerEvents: "auto"
                                                }
                                            })}
                                            disabled={isSchemesTableDataLoading ||
                                                Object.keys(selectedSchemesObj).length === 0}
                                        >
                                            {isRMLikeRole(role) ? "Recommend" : "Proceed"}
                                        </Button>
                                    )}
                            </Box>
                            {/* <Box
                                sx={{
                                    marginTop: "15px",
                                    marginLeft: { lg: "30px" },
                                    display: "flex",
                                    gap: "15px",
                                    flexWrap: "wrap",
                                }}
                            >
                                {appliedFundsFilters.map((obj) => (
                                    <Button
                                        key={obj.filterTypeValue}
                                        variant="contained"
                                        startIcon={<CrossIcon />}
                                        onClick={() => handleFilterLeavesClick(
                                            obj.filterType,
                                            obj.filterTypeValue,
                                            obj.value
                                        )}
                                        sx={(theme: Theme) => ({
                                            borderRadius: "61px",
                                            backgroundColor: theme.palette.primary[50],
                                            padding: "7px 16px",
                                            color: theme.palette.text.primary,
                                            whiteSpace: "nowrap",
                                            boxShadow: "none",
                                            ...theme.typography.subtitle_400,
                                            "& .MuiButton-startIcon": {
                                                paddingTop: "2px"
                                            },
                                            "&:hover": {
                                                backgroundColor: theme.palette.primary[100],
                                                boxShadow: "none"
                                            },
                                            "&.Mui-disabled": {
                                                opacity: 0.5
                                            }
                                        })}
                                        disabled={isSchemesTableDataLoading}
                                    >
                                        {obj.label}
                                    </Button>
                                ))}
                            </Box> */}
                            <Box
                                sx={{ marginTop: "5px", height: '93%', width: '100%' }}
                            >
                                {(initialRouter[0] !== 'product') && !!selectedSchemesTableData.length && (
                                    <Box
                                        sx={(theme: Theme) => ({
                                            padding: { xs: "10px" },
                                            backgroundColor: theme.palette.primary[100],
                                            display: "flex",
                                            flexWrap: "wrap",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mt: "10px"
                                        })}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                columnGap: "10px",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Box
                                                component="button"
                                                sx={{
                                                    display: "flex",
                                                    cursor: "pointer",
                                                    background: "none",
                                                    border: "none",
                                                    padding: 0,
                                                    '&:focus-visible': {
                                                        outline: (theme: Theme) => `2px solid ${theme.palette.primary[500]}`,
                                                        outlineOffset: '2px',
                                                        borderRadius: '4px',
                                                    }
                                                }}
                                                aria-label={`Clear all ${selectedSchemesTableData.length} selected schemes`}
                                                onClick={handleClearSelectedSchemes}
                                                onKeyDown={(e: React.KeyboardEvent) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleClearSelectedSchemes();
                                                    }
                                                }}
                                            >
                                                <ToggleBox aria-hidden="true" />
                                            </Box>
                                            <Typography
                                                variant="overline_600"
                                                aria-live="polite"
                                                aria-atomic="true"
                                                sx={(theme: Theme) => ({
                                                    color: theme.palette.primary[500],
                                                    lineHeight: "17.5px"
                                                })}
                                            >
                                                {selectedSchemesTableData.length} Selected
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="overline_600"
                                            sx={(theme: Theme) => ({
                                                color: theme.palette.primary[500],
                                                cursor: "pointer",
                                                lineHeight: "17.5px",
                                                textAlign: "right",
                                                '&:focus-visible': {
                                                    outline: `2px solid ${theme.palette.primary[500]}`,
                                                    outlineOffset: '2px',
                                                    borderRadius: '2px',
                                                }
                                            })}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => setSelectedSchemesDialogVisible(true)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setSelectedSchemesDialogVisible(true);
                                                }
                                            }}
                                        >
                                            View<Hidden only="xs"> Selected Schemes</Hidden>
                                        </Typography>
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        marginTop: schemesTableData.length ? "0px" : "50px",
                                        height: '100%',
                                    }}
                                >
                                    <CustomTable
                                        isLoading={isSchemesTableDataLoading}
                                        tableContainerStyles={{ height: "90%", overflow: 'auto', paddingBottom: '40px' }}
                                        tableBody={
                                            <MutualFundTableBody
                                                tableBodyData={schemesTableData}
                                                tableBodyRowCustomStyles={{
                                                    '& td:first-of-type': {
                                                        paddingLeft: "30px"
                                                    },
                                                    '& td': {
                                                        paddingTop: "8px",
                                                        paddingBottom: "8px",
                                                    },
                                                }}
                                                handleCheckboxClick={handleSchemeTableCheckboxClick}
                                                handleSchemeClick={handleSchemeTableSchemeClick}
                                                selectedCategory={selectedCategory}
                                            />
                                        }
                                        tableData={schemesTableData}
                                        tableBodyLoaderRowCustomStyles={{
                                            '& td:first-of-type': {
                                                paddingLeft: "30px"
                                            },
                                        }}
                                        tableHeaderData={tableHeaderData}
                                        tableHeadRowCustomStyles={{
                                            '& th:first-of-type': {
                                                paddingLeft: "30px",
                                            },
                                            '& th': {
                                                paddingTop: "8px",
                                                paddingBottom: "8px",
                                            },
                                            '& .MuiTypography-subtitle_500': {
                                                fontSize: "12px"
                                            }
                                        }}
                                        handleSort={handleSchemesTableSort}
                                        tableDataCount={schemesTablePaginationObj.totalRows}
                                        rowsPerPage={schemesTablePaginationObj.rowsPerPage}
                                        rowsPerPageOptions={[10, 20, 30, 40]}
                                        handleRowsPerPageChange={handleSchemesTableRowsPerPageChange}
                                        handlePageChange={handleSchemesTablePageChange}
                                        currentPage={schemesTablePaginationObj.currentPage}
                                        order={schemesTableSortingObj.order}
                                        orderBy={schemesTableSortingObj.orderBy}
                                        manualResponsiveBody={true}
                                        hideResponsiveHeader={true}
                                        ManualResponsiveBodyComponent={
                                            <SchemeBoxResponsiveBody
                                                tableBodyData={schemesTableData}
                                                tableBodyRowCustomStyles={{
                                                    '& td:first-of-type': {
                                                        paddingLeft: "30px"
                                                    }
                                                }}
                                                handleCheckboxClick={handleSchemeTableCheckboxClick}
                                                handleSchemeClick={handleSchemeTableSchemeClick}
                                                tableHeaderData={tableHeaderData}
                                                selectedCategory={selectedCategory}
                                            />
                                        }
                                        paginationStyles={{
                                            marginRight: 0,
                                            background: 'white',
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            width: '100%',
                                            marginBottom: '-15px',
                                            '& .MuiTablePagination-selectLabel': {
                                                marginTop: '5px',
                                                marginBottom: '5px'
                                            }
                                        }}
                                        headSticky
                                        isScrollTable
                                    />
                                    <Box
                                        sx={{
                                            marginTop: "10px",
                                            marginRight: 0,
                                            background: 'white',
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            width: '100%',
                                            marginBottom: '-15px',
                                            '& .MuiTablePagination-selectLabel': {
                                                marginTop: '5px',
                                                marginBottom: '5px'
                                            }
                                        }}
                                    >
                                        <TablePaginationComponent
                                            tableDataCount={schemesTablePaginationObj.totalRows}
                                            handlePageChange={handleSchemesTablePageChange}
                                            handleRowsPerPageChange={handleSchemesTableRowsPerPageChange}
                                            rowsPerPage={schemesTablePaginationObj.rowsPerPage}
                                            rowsPerPageOptions={[10, 20, 30, 40]}
                                            currentPage={schemesTablePaginationObj.currentPage}
                                        />

                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        {/* } */}
                    </Grid >
                </Grid >
            </Box >
            <SelectedSchemesDialog
                isVisible={isSelectedSchemesDialogVisible}
                onClose={handleSelectedSchemesDialogClose}
                tableData={selectedSchemesTableData}
                sortingObj={selectedSchemesTableSortingObj}
                setSortingObj={setSelectedSchemesTableSortingObj}
                onCheckboxClick={handleSelectedSchemesTableCheckboxClick}
                onSchemeClick={handleSelectedSchemesTableSchemeClick}
                clientCode={clientCode}
                selectedCategory={selectedCategory}
            />
            {isSearchSchemesDialogVisible && (
                <SearchSchemesDialog
                    isVisible={isSearchSchemesDialogVisible}
                    onClose={handleSearchSchemesDialogClose}
                    handleProceedClick={handleSearchSchemesDialogProceedClick}
                    clientCode={clientCode}
                />
            )}
            <FiveSchemesDialog
                isVisible={isFiveSchemesDialogVisible}
                onClose={handleFiveSchemesDialogClose}
            />
        </>
    )
}